import { apiPost, apiGet } from './api';

class SocialMediaAPI {
  constructor() {
    this.baseURL = 'https://social-media-backend.andrewrkloc.workers.dev';
  }

  // Facebook OAuth - Completely rewritten for reliability
  async connectFacebook() {
    try {
      console.log('🔄 Starting Facebook OAuth process...');
      
      // Step 1: Get OAuth URL from backend
      const response = await apiPost('/api/social/connect-facebook');
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to get OAuth URL');
      }

      console.log('✅ Got OAuth URL:', response.authUrl);

      // Step 2: Open popup with proper error handling
      const popup = this.openPopup(response.authUrl);
      
      if (!popup) {
        throw new Error('Popup blocked by browser. Please allow popups for this site.');
      }

      // Step 3: Wait for OAuth completion with timeout
      const result = await this.waitForOAuthCompletion(popup);
      
      console.log('✅ OAuth completed successfully');
      return result;

    } catch (error) {
      console.error('❌ Facebook OAuth failed:', error);
      throw error;
    }
  }

  // Open popup with proper configuration
  openPopup(url) {
    const width = 600;
    const height = 700;
    const left = window.screenX + (window.outerWidth - width) / 2;
    const top = window.screenY + (window.outerHeight - height) / 2;

    const popup = window.open(
      url,
      'facebook-oauth',
      `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no`
    );

    if (!popup || popup.closed || typeof popup.closed === 'undefined') {
      return null;
    }

    return popup;
  }

  // Wait for OAuth completion with proper error handling
  waitForOAuthCompletion(popup) {
    return new Promise((resolve, reject) => {
      let timeoutId;
      let intervalId;
      let hasProcessedCallback = false;

      // Set timeout for OAuth process
      timeoutId = setTimeout(() => {
        clearInterval(intervalId);
        popup.close();
        if (!hasProcessedCallback) {
          reject(new Error('OAuth timeout - please try again'));
        }
      }, 300000); // 5 minutes timeout

      // Check popup status
      intervalId = setInterval(() => {
        try {
          // Check if popup is closed
          if (popup.closed) {
            clearTimeout(timeoutId);
            clearInterval(intervalId);
            
            // Check if we have a token
            const token = localStorage.getItem('facebookAccessToken');
            if (token && token.length > 10) { // Ensure token is valid
              console.log('✅ OAuth completed with valid token');
              resolve({ success: true, accessToken: token });
            } else {
              console.log('❌ OAuth completed but no valid token found');
              reject(new Error('OAuth was cancelled or failed - no valid token received'));
            }
            return;
          }

          // Try to access popup location (will throw if cross-origin)
          const popupUrl = popup.location.href;
          
          // Check if we're on the callback URL
          if (popupUrl.includes('facebook-callback')) {
            clearTimeout(timeoutId);
            clearInterval(intervalId);
            
            // Give the popup a moment to process
            setTimeout(() => {
              const token = localStorage.getItem('facebookAccessToken');
              if (token) {
                resolve({ success: true, accessToken: token });
              } else {
                reject(new Error('OAuth callback failed to store token'));
              }
            }, 1000);
          }

        } catch (e) {
          // Cross-origin error - popup is still on Facebook
          // This is normal, continue waiting
        }
      }, 1000);

      // Listen for postMessage from popup
      const messageHandler = (event) => {
        if (event.data && event.data.type === 'FACEBOOK_AUTH_SUCCESS') {
          hasProcessedCallback = true;
          clearTimeout(timeoutId);
          clearInterval(intervalId);
          window.removeEventListener('message', messageHandler);
          popup.close();
          
          // Store the token and data in localStorage
          console.log('✅ Storing Facebook token and data in localStorage...');
          localStorage.setItem('facebookAccessToken', event.data.accessToken);
          localStorage.setItem('facebookPages', JSON.stringify(event.data.pages || []));
          localStorage.setItem('facebookUser', JSON.stringify(event.data.userInfo || {}));
          
          resolve({ success: true, accessToken: event.data.accessToken });
        } else if (event.data && event.data.type === 'FACEBOOK_AUTH_ERROR') {
          hasProcessedCallback = true;
          clearTimeout(timeoutId);
          clearInterval(intervalId);
          window.removeEventListener('message', messageHandler);
          popup.close();
          reject(new Error(event.data.error || 'OAuth failed'));
        }
      };

      window.addEventListener('message', messageHandler);
    });
  }

  // Get Facebook pages with better error handling
  async getFacebookPages(accessToken) {
    try {
      console.log('🔄 Fetching Facebook pages...');
      
      if (!accessToken) {
        throw new Error('No access token available');
      }

      const response = await apiGet(`/api/social/get-facebook-pages?access_token=${accessToken}`);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch Facebook pages');
      }

      console.log('✅ Found Facebook pages:', response.data.length);
      return response.data || [];
      
    } catch (error) {
      console.error('❌ Error fetching Facebook pages:', error);
      throw error;
    }
  }

  // Get Instagram accounts with better error handling
  async getInstagramAccounts(pageId, pageAccessToken) {
    try {
      console.log('🔄 Fetching Instagram accounts for page:', pageId);
      
      if (!pageId || !pageAccessToken) {
        throw new Error('Missing page ID or access token');
      }

      const response = await apiGet(`/api/social/get-instagram-accounts?pageId=${pageId}&pageAccessToken=${pageAccessToken}`);
      
      if (!response.success) {
        throw new Error(response.error || 'Failed to fetch Instagram accounts');
      }

      console.log('✅ Found Instagram accounts:', response.data.length);
      return response.data || [];
      
    } catch (error) {
      console.error('❌ Error fetching Instagram accounts:', error);
      return [];
    }
  }

  // Post to Facebook with improved error handling
  async postToFacebook(pageId, message, mediaUrl = null, scheduledTime = null) {
    try {
      console.log('🔄 Posting to Facebook page...');
      
      // Get the user access token
      const userAccessToken = localStorage.getItem('facebookAccessToken');
      if (!userAccessToken) {
        throw new Error('Facebook not connected. Please connect your Facebook account first.');
      }

      console.log('🔍 Debug - Token being sent:', {
        hasToken: !!userAccessToken,
        tokenLength: userAccessToken.length,
        tokenStart: userAccessToken.substring(0, 10) + '...',
        pageId,
        messageLength: message.length
      });

      if (!pageId || !message) {
        throw new Error('Missing page ID or message');
      }

      const response = await apiPost('/api/social/post-to-facebook', {
        pageId,
        userAccessToken: userAccessToken,
        message,
        mediaUrl,
        scheduledTime
      });

      if (response.success) {
        console.log('✅ Posted to Facebook successfully');
        return { success: true, postId: response.postId, message: 'Posted to Facebook successfully!' };
      } else {
        throw new Error(response.error || 'Failed to post to Facebook');
      }
    } catch (error) {
      console.error('❌ Facebook posting error:', error);
      throw error;
    }
  }

  // Post to Instagram with improved error handling
  async postToInstagram(pageId, message, mediaUrl) {
    try {
      console.log('🔄 Posting to Instagram...');
      
      const userAccessToken = localStorage.getItem('facebookAccessToken');
      if (!userAccessToken) {
        throw new Error('Facebook not connected. Please connect your Facebook account first.');
      }

      if (!pageId || !message) {
        throw new Error('Missing page ID or message');
      }

      // Instagram requires media, so if no media URL provided, use a placeholder
      const finalMediaUrl = mediaUrl || 'https://via.placeholder.com/600x400/FF6B6B/FFFFFF?text=Instagram+Post';

      console.log('🔍 Debug - Instagram posting:', {
        hasToken: !!userAccessToken,
        tokenLength: userAccessToken ? userAccessToken.length : 0,
        pageId,
        messageLength: message ? message.length : 0,
        hasMedia: !!finalMediaUrl
      });

      const response = await apiPost('/api/social/post-to-instagram', {
        userAccessToken,
        pageId,
        message,
        mediaUrl: finalMediaUrl
      });

      if (response.success) {
        console.log('✅ Posted to Instagram successfully');
        return { success: true, postId: response.postId, message: 'Posted to Instagram successfully!' };
      } else {
        throw new Error(response.error || 'Failed to post to Instagram');
      }
    } catch (error) {
      console.error('❌ Instagram posting error:', error);
      throw error;
    }
  }

  // Check if Facebook is connected
  isFacebookConnected() {
    const token = localStorage.getItem('facebookAccessToken');
    return !!token;
  }

  // Disconnect Facebook
  disconnectFacebook() {
    localStorage.removeItem('facebookAccessToken');
    localStorage.removeItem('facebookPages');
    localStorage.removeItem('facebookUser');
    console.log('✅ Facebook disconnected');
  }

  // Get connected accounts from localStorage
  getConnectedAccounts() {
    const facebookPages = JSON.parse(localStorage.getItem('facebookPages') || '[]');
    const instagramAccounts = JSON.parse(localStorage.getItem('instagramAccounts') || '[]');
    
    return {
      facebook: facebookPages,
      instagram: instagramAccounts
    };
  }

  // Save connected accounts to localStorage
  saveConnectedAccounts(accounts) {
    localStorage.setItem('facebookPages', JSON.stringify(accounts.facebook || []));
    localStorage.setItem('instagramAccounts', JSON.stringify(accounts.instagram || []));
  }

    // Debug method to check OAuth status
  debugOAuthStatus() {
    const token = localStorage.getItem('facebookAccessToken');
    const pages = localStorage.getItem('facebookPages');
    const user = localStorage.getItem('facebookUser');

    console.log('🔍 OAuth Debug Info:');
    console.log('- Has token:', !!token);
    console.log('- Token length:', token ? token.length : 0);
    console.log('- Token preview:', token ? token.substring(0, 20) + '...' : 'none');
    console.log('- Has pages:', !!pages);
    console.log('- Has user:', !!user);

    return {
      hasToken: !!token,
      hasPages: !!pages,
      hasUser: !!user,
      tokenLength: token ? token.length : 0
    };
  }
}

export default new SocialMediaAPI();
